import { useMemo, useState } from 'react';
import { Info, ShieldCheck } from 'lucide-react';

import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { DestructiveActionChip } from '@/ui/DestructiveActionChip';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Skeleton } from '@/ui/base/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import { useDres } from '../hooks/useDres';
import { useHistoricoExportacaoFiscal } from '../hooks/useSofExtras';
import { competenciaAtual, MESES } from './mesesRelatorio';
import { SofHeader } from './SofHeader';
import { SofTabsNav } from './SofTabsNav';

/** FIN-031.5/031.6: o Alert de "registro definitivo" e o filtro de
 * Competência/DRE são compartilhados pelas 4 abas de `ExportacoesFiscaisPage`
 * no protótipo (ficam fora do `<Tabs>`) — por isso aparecem aqui também,
 * mesmo esta tela não usando `dreId` (histórico não tem DRE por linha) e
 * `mes` não filtrar `historico` (o protótipo também não filtra: o filtro de
 * período específico desta aba é o `DateRangePresetFilter` "Período de
 * geração" dentro do card). */
export function SofHistoricoPage() {
  const { data, isLoading, isError, refetch } = useHistoricoExportacaoFiscal();
  const { data: dres } = useDres();
  const [mes, definirMes] = useState(competenciaAtual());
  const [dreId, definirDreId] = useState<string | null>(null);
  const [periodo, definirPeriodo] = useState<DateRangeValue>({ preset: 'ALL' });

  const historico = useMemo(() => {
    const linhas = data ?? [];
    const { from, to } = resolveDateRange(periodo);
    if (!from && !to) return linhas;
    return linhas.filter((l) => {
      const data = l.geradoEm.slice(0, 10);
      if (from && data < from) return false;
      if (to && data > to) return false;
      return true;
    });
  }, [data, periodo]);

  const campos: FilterFieldConfig[] = [
    {
      key: 'mes',
      label: 'Competência',
      type: 'select',
      options: MESES,
      showAllOption: false,
      width: 'w-full sm:w-[200px]',
    },
  ];

  return (
    <div className="space-y-6">
      <SofHeader />

      <SofTabsNav />

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="flex flex-wrap items-center gap-2 text-sm">
          <DestructiveActionChip label="Registro definitivo" tone="warning" />
          <span>
            Exportações em <strong>PDF</strong> geram protocolo SEI com hash SHA-256 e ficam
            imutáveis no histórico. CSV e XLSX são apenas pré-visualização de auditoria.
          </span>
        </AlertDescription>
      </Alert>

      <FilterBar
        fields={campos}
        values={{ mes }}
        onChange={(_, valor) => definirMes(valor)}
        onClear={() => {
          definirMes(competenciaAtual());
          definirDreId(null);
        }}
        hasActiveFilters={mes !== competenciaAtual() || dreId !== null}
      >
        <div className="space-y-1 sm:w-[280px]">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">DRE</Label>
          <SearchableSelect
            value={dreId}
            onChange={definirDreId}
            options={(dres ?? []).map((d) => ({ value: d.id, label: d.nome ?? d.codigo ?? d.id }))}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar por código ou nome…"
            clearable={false}
          />
        </div>
      </FilterBar>

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <p className="text-base font-semibold">Histórico de exportações</p>
              <p className="text-xs text-muted-foreground">
                {historico.length} {historico.length === 1 ? 'registro' : 'registros'} no período
                selecionado
              </p>
            </div>
            <DateRangePresetFilter
              label="Período de geração"
              value={periodo}
              onChange={definirPeriodo}
              width="w-full sm:w-[220px]"
            />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : historico.length === 0 ? (
              <EmptyState
                icon={ShieldCheck}
                title="Nenhuma exportação registrada"
                description="As exportações aparecem aqui automaticamente."
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Formato</TableHead>
                      <TableHead>Competência</TableHead>
                      <TableHead className="text-right">Registros</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>SEI</TableHead>
                      <TableHead>Gerado em</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historico.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="font-mono text-xs">{h.numero}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{h.tipo}</Badge>
                        </TableCell>
                        <TableCell>{h.formato}</TableCell>
                        <TableCell>
                          {new Date(`${h.competencia}T00:00:00`).toLocaleDateString('pt-BR', {
                            month: '2-digit',
                            year: 'numeric',
                          })}
                        </TableCell>
                        <TableCell className="text-right">{h.registros}</TableCell>
                        <TableCell className="text-right">
                          <MoneyDisplay value={h.valor} />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{h.protocoloSei ?? '—'}</TableCell>
                        <TableCell className="text-xs">
                          {new Intl.DateTimeFormat('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          }).format(new Date(h.geradoEm))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default SofHistoricoPage;
